import useAxiosPrivate from "./useAxiosPrivate";
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

const usePrefetchItems = (apiUrl, itemName) => {
  const queryClient = useQueryClient();
  const axiosPrivate = useAxiosPrivate();

  // Function to obtain items by IDs
  const fetchItemsByIds = useCallback(
    async (ids) => {
      const cleanedIds = typeof ids === 'string' ? ids.replace(/['"]/g, '') : String(ids).replace(/['"]/g, '');
      const response = await axiosPrivate.get(
        `/${apiUrl}?${itemName}_id=${cleanedIds}`,
      );
      return response.data.data || [];
    },
    [axiosPrivate, apiUrl, itemName],
  );

  // Function to obtain items by keywords
  const fetchItemsByKeywords = useCallback(
    async (keywords, customerId) => {
      let query = `/${apiUrl}?q=${keywords}`;
      if (customerId) {
        query += `&customer_id=${customerId}`;
      }
      const response = await axiosPrivate.get(query);
      return response.data.data || [];
    },
    [axiosPrivate, apiUrl],
  );

  // Function to initialize item data with specific IDs
  const initAllItems = useCallback(
    async (initialItemsIds) => {
      const items = await fetchItemsByIds(initialItemsIds);
      queryClient.setQueryData(
        [`list${itemName.charAt(0).toUpperCase() + itemName.slice(1)}`],
        items,
      );
    },
    [queryClient, itemName, fetchItemsByIds],
  );

  // Function to add new items to the existing list
  const addItems = useCallback(
    async (query, options = {}) => {
        const { customerId, onFetchStart, onFetchEnd } = options;
      onFetchStart?.();
      let newItems = [];

      if (Array.isArray(query)) {
        // Processing by IDs
        newItems = await fetchItemsByIds(query);
      } else if (typeof query === "string") {
        // Processing by keywords
        newItems = await fetchItemsByKeywords(query, customerId);
      }

      // Retrieve existing cache items
      const existingItemsKey = `list${
        itemName.charAt(0).toUpperCase() + itemName.slice(1)
      }`;
      const existingItemsData =
        queryClient.getQueryData([existingItemsKey]) || [];

      // Filter items already present
      const itemsToAdd = newItems.filter(
        (newItem) =>
          !existingItemsData.some(
            (existingItem) =>
              existingItem[`${itemName}_id`] === newItem[`${itemName}_id`],
          ),
      );
      if (Array.isArray(itemsToAdd) && itemsToAdd.length > 0) {
        queryClient.setQueryData(
          [existingItemsKey],
          [...existingItemsData, ...itemsToAdd],
        );
      }
      onFetchEnd?.();
    },
    [queryClient, itemName, fetchItemsByIds, fetchItemsByKeywords],
  );

  return { initAllItems, addItems };
};

export default usePrefetchItems;
